<script lang="ts" setup>
import { z } from 'zod'
import type { Form, FormSubmitEvent } from '#ui/types'

const { t } = useI18n()
const sanctumFetch = useSanctumClient()

const linkSent = ref(false)

const schema = z.object({
  email: z.string().email(),
})

type Schema = z.output<typeof schema>

const form = ref<Form<Schema>>()
const state = reactive<Schema>({
  email: '',
})

async function onSubmit(event: FormSubmitEvent<Schema>) {
  form.value?.clear()

  const email = event.data.email

  try {
    await sanctumFetch('/api/forgot-password', {
      method: 'POST',
      body: { email },
    })

    linkSent.value = true
  } catch (error) {
    const err = useSanctumError(error)

    if (err.isValidationError) {
      form.value?.setErrors(err.bag)
    }
  }
}
</script>

<template>
  <UAlert
    v-if="linkSent"
    color="info"
    variant="subtle"
    icon="i-lucide-mail-check"
    :title="t('auth.reset_link_sent')"
    :description="t('auth.reset_link_sent_message')"
  />

  <UForm
    v-else
    ref="form"
    :schema="schema"
    :state="state"
    class="sm:min-w-80 md:min-w-96 max-w-md"
    @submit="onSubmit"
  >
    <div class="flex flex-col gap-y-4">
      <span class="text-sm">
        {{ t('auth.forgot_password_message') }}
      </span>

      <UFormField :label="t('fields.email')" name="email">
        <UInput
          v-model="state.email"
          icon="i-heroicons-at-symbol"
          class="w-full"
          trailing
        />
      </UFormField>

      <div class="flex justify-end">
        <UButton type="submit">
          {{ t('auth.send_reset_link') }}
        </UButton>
      </div>
    </div>
  </UForm>
</template>
